/**
 * useMenu Hook - Manage menu state and operations
 * Created: 1405/02/17
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { MenuItem, MenuConfiguration, MergedMenuItem } from '../types';
import {
  setRuntimeConfig,
  isRuntimeConfigActive
} from '../registry';
import { buildMenuTree } from '../utils/menu-builder';
import menuService from '../services/menuService';

interface UseMenuReturn {
  mergedMenu: MenuItem[];
  menuTree: MergedMenuItem[];

  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: Error | null;

  // Operations
  refreshMenu: () => void;
  updateMenu: (items: MenuItem[]) => void;

  // Runtime config
  isRuntimeActive: boolean;
}

export const useMenu = (): UseMenuReturn => {
  const [runtimeConfig, setLocalConfig] = useState<MenuConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch runtime configuration from API
  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const config = await menuService.getActiveConfiguration();
      setLocalConfig(config);
    } catch (err) {
      // منوی رانتایم تنها منبع است — در صورت خطا، state خطا ست می‌شود
      // تا سایدبار پیام خطا + تلاش مجدد نشان دهد (نه منوی قدیمی گمراه‌کننده)
      console.log('Failed to load runtime menu configuration');
      setLocalConfig(null);
      setError(err instanceof Error ? err : new Error('خطا در بارگذاری منو'));
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  
  // Update global cache when local config changes
  useEffect(() => {
    setRuntimeConfig(runtimeConfig);
  }, [runtimeConfig]);

  // Computed values — منوی رانتایم تنها منبع است
  const mergedMenu = useMemo(() => {
    const hasRuntime =
      runtimeConfig !== null &&
      runtimeConfig.isActive &&
      Array.isArray(runtimeConfig.items) &&
      runtimeConfig.items.length > 0;
    if (hasRuntime) {
      return runtimeConfig!.items.filter(item => item.isActive);
    }
    return [];
  }, [runtimeConfig]);

  const menuTree = useMemo(() => {
    return buildMenuTree(mergedMenu);
  }, [mergedMenu]);

  const isRuntimeActive = useMemo(() => {
    return runtimeConfig?.isActive || false;
  }, [runtimeConfig]);
  
  // Refresh menu
  const refreshMenu = useCallback(() => {
    fetchConfig();
  }, [fetchConfig]);
  
  // Update menu locally (without saving to DB)
  const updateMenu = useCallback((items: MenuItem[]) => {
    const tempConfig: MenuConfiguration = {
      id: 'temp',
      name: 'Temporary',
      items,
      isActive: true,
      modifiedBy: 'current-user',
      modifiedAt: new Date(),
    };
    setLocalConfig(tempConfig);
  }, []);
  
  return {
    mergedMenu,
    menuTree,
    isLoading,
    isError,
    error,
    refreshMenu,
    updateMenu,
    isRuntimeActive,
  };
};

export default useMenu;
